import * as React from 'react'
import { type QueryClient, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ListPage } from '@/components/templates/list-page'
import { Alert } from '@/components/ui/alert'
import { getJSON } from '@/lib/api'
import {
  archiveMembershipLevel,
  createMembershipLevel,
  deleteMembershipLevel,
  membershipLevelFutureRenewalWarning,
  membershipLevelRosterCapWarning,
  membershipLevelActionError,
  reorderMembershipLevels,
  restoreMembershipLevel,
  updateMembershipLevel,
} from '@/pages/MembershipLevelsPage.api'
import { MembershipLevelPageDialogs } from '@/pages/MembershipLevelPageDialogs'
import {
  membershipLevelColumns,
  membershipLevelRowActions,
  movedMembershipLevels,
} from '@/pages/MembershipLevelsPage.table'
import type {
  MembershipLevelFutureRenewalConfirmation,
  MembershipLevelRosterCapConfirmation,
  MembershipLevel,
  MembershipLevelDialogState,
  MembershipLevelForm,
  MembershipLevelsResponse,
} from '@/pages/MembershipLevelsPage.types'
import type { TaxRatesResponse } from '@/pages/TaxSettingsPage'

export function MembershipLevelsPage() {
  const queryClient = useQueryClient()
  const [dialogState, setDialogState] = React.useState<MembershipLevelDialogState>(null)
  const [archiveTarget, setArchiveTarget] = React.useState<MembershipLevel | null>(null)
  const [deleteTarget, setDeleteTarget] = React.useState<MembershipLevel | null>(null)
  const [futureRenewalConfirmation, setFutureRenewalConfirmation] =
    React.useState<MembershipLevelFutureRenewalConfirmation | null>(null)
  const [rosterCapConfirmation, setRosterCapConfirmation] =
    React.useState<MembershipLevelRosterCapConfirmation | null>(null)
  const levelsQuery = useQuery({
    queryKey: ['membership-levels'],
    queryFn: () => getJSON<MembershipLevelsResponse>('/api/v1/membership_levels'),
    retry: false,
  })
  const taxRatesQuery = useQuery({
    queryKey: ['tax-rates'],
    queryFn: () => getJSON<TaxRatesResponse>('/api/v1/tax_rates'),
    enabled: dialogState !== null,
    retry: false,
  })
  const mutations = useMembershipLevelMutations({
    queryClient,
    setDialogState,
    setArchiveTarget,
    setDeleteTarget,
    setFutureRenewalConfirmation,
    setRosterCapConfirmation,
  })

  return (
    <MembershipLevelsView
      levels={levelsQuery.data?.membership_levels ?? []}
      tableState={membershipLevelsTableState(levelsQuery.isLoading, levelsQuery.isError)}
      dialogState={dialogState}
      archiveTarget={archiveTarget}
      deleteTarget={deleteTarget}
      futureRenewalConfirmation={futureRenewalConfirmation}
      rosterCapConfirmation={rosterCapConfirmation}
      mutations={mutations}
      taxRates={taxRatesQuery.data?.tax_rates ?? []}
      setDialogState={setDialogState}
      setArchiveTarget={setArchiveTarget}
      setDeleteTarget={setDeleteTarget}
      setFutureRenewalConfirmation={setFutureRenewalConfirmation}
      setRosterCapConfirmation={setRosterCapConfirmation}
    />
  )
}

function MembershipLevelsView({
  levels,
  tableState,
  dialogState,
  archiveTarget,
  deleteTarget,
  futureRenewalConfirmation,
  rosterCapConfirmation,
  mutations,
  taxRates,
  setDialogState,
  setArchiveTarget,
  setDeleteTarget,
  setFutureRenewalConfirmation,
  setRosterCapConfirmation,
}: MembershipLevelsViewProps) {
  const moveLevel = (level: MembershipLevel, direction: 'up' | 'down') => {
    const nextLevels = movedMembershipLevels(levels, level, direction)
    if (!nextLevels) return

    mutations.reorderLevels.mutate(nextLevels)
  }
  const rowActionError = mutations.reorderLevels.error ?? mutations.restoreLevel.error

  return (
    <>
      <ListPage
        title="Membership levels"
        primaryAction={{ label: 'New level', onClick: () => setDialogState({ mode: 'new' }) }}
        toolbar={
          rowActionError ? <Alert>{membershipLevelActionError(rowActionError)}</Alert> : undefined
        }
        table={{
          rows: levels,
          columns: membershipLevelColumns,
          getRowId: (level) => level.id,
          getRowLabel: (level) => level.name,
          onRowOpen: (level) => setDialogState({ mode: 'edit', level }),
          actions: (level) =>
            membershipLevelRowActions({
              level,
              levels,
              moveLevel,
              restoreLevel: mutations.restoreLevel.mutate,
              setDialogState,
              setArchiveTarget,
              setDeleteTarget,
            }),
          state: tableState,
          loadingTitle: 'Loading Membership Levels',
          emptyTitle: 'No Membership Levels yet',
          emptyDescription: 'Create the first level members can hold.',
          errorTitle: 'Membership Levels unavailable',
          errorDescription: 'Refresh and try again.',
        }}
      />

      <MembershipLevelPageDialogs
        dialogState={dialogState}
        archiveTarget={archiveTarget}
        deleteTarget={deleteTarget}
        futureRenewalConfirmation={futureRenewalConfirmation}
        rosterCapConfirmation={rosterCapConfirmation}
        mutations={mutations}
        taxRates={taxRates}
        levels={levels}
        setDialogState={setDialogState}
        setArchiveTarget={setArchiveTarget}
        setDeleteTarget={setDeleteTarget}
        onSubmit={(form) =>
          submitMembershipLevelDialog(dialogState!, form, mutations, setDialogState, {
            setFutureRenewalConfirmation,
            setRosterCapConfirmation,
          })
        }
        onCloseFutureRenewal={() =>
          closeFutureRenewalConfirmation(mutations, setFutureRenewalConfirmation)
        }
        onConfirmFutureRenewal={() =>
          confirmFutureRenewalChange(futureRenewalConfirmation!, mutations)
        }
        onCloseRosterCap={() => closeRosterCapConfirmation(mutations, setRosterCapConfirmation)}
        onConfirmRosterCap={() => confirmRosterCapChange(rosterCapConfirmation!, mutations)}
      />
    </>
  )
}

function useMembershipLevelMutations({
  queryClient,
  setDialogState,
  setArchiveTarget,
  setDeleteTarget,
  setFutureRenewalConfirmation,
  setRosterCapConfirmation,
}: MembershipLevelMutationOptions) {
  const createLevel = useMutation({
    mutationFn: createMembershipLevel,
    onSuccess: async () => {
      setDialogState(null)
      await queryClient.invalidateQueries({ queryKey: ['membership-levels'] })
    },
  })
  const updateLevel = useMutation({
    mutationFn: ({
      level,
      form,
      confirmFutureOnlyChange,
      confirmRosterCapChange,
    }: {
      level: MembershipLevel
      form: MembershipLevelForm
      confirmFutureOnlyChange?: boolean
      confirmRosterCapChange?: boolean
    }) => updateMembershipLevel(level, form, { confirmFutureOnlyChange, confirmRosterCapChange }),
    onSuccess: async () => {
      setDialogState(null)
      setFutureRenewalConfirmation(null)
      setRosterCapConfirmation(null)
      await queryClient.invalidateQueries({ queryKey: ['membership-levels'] })
    },
  })
  const archiveLevel = useMutation({
    mutationFn: archiveMembershipLevel,
    onSuccess: async () => {
      setArchiveTarget(null)
      await queryClient.invalidateQueries({ queryKey: ['membership-levels'] })
    },
  })
  const restoreLevel = useMutation({
    mutationFn: restoreMembershipLevel,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['membership-levels'] })
    },
  })
  const deleteLevel = useMutation({
    mutationFn: deleteMembershipLevel,
    onSuccess: async () => {
      setDeleteTarget(null)
      await queryClient.invalidateQueries({ queryKey: ['membership-levels'] })
    },
  })
  const reorderLevels = useMutation({
    mutationFn: reorderMembershipLevels,
    onSuccess: (data) => {
      queryClient.setQueryData(['membership-levels'], data)
    },
  })

  return { createLevel, updateLevel, archiveLevel, restoreLevel, deleteLevel, reorderLevels }
}

interface MembershipLevelsViewProps {
  levels: MembershipLevel[]
  tableState: 'ready' | 'loading' | 'error'
  dialogState: MembershipLevelDialogState
  archiveTarget: MembershipLevel | null
  deleteTarget: MembershipLevel | null
  futureRenewalConfirmation: MembershipLevelFutureRenewalConfirmation | null
  rosterCapConfirmation: MembershipLevelRosterCapConfirmation | null
  mutations: MembershipLevelMutations
  taxRates: TaxRatesResponse['tax_rates']
  setDialogState: React.Dispatch<React.SetStateAction<MembershipLevelDialogState>>
  setArchiveTarget: React.Dispatch<React.SetStateAction<MembershipLevel | null>>
  setDeleteTarget: React.Dispatch<React.SetStateAction<MembershipLevel | null>>
  setFutureRenewalConfirmation: React.Dispatch<
    React.SetStateAction<MembershipLevelFutureRenewalConfirmation | null>
  >
  setRosterCapConfirmation: React.Dispatch<
    React.SetStateAction<MembershipLevelRosterCapConfirmation | null>
  >
}

interface MembershipLevelMutationOptions {
  queryClient: QueryClient
  setDialogState: React.Dispatch<React.SetStateAction<MembershipLevelDialogState>>
  setArchiveTarget: React.Dispatch<React.SetStateAction<MembershipLevel | null>>
  setDeleteTarget: React.Dispatch<React.SetStateAction<MembershipLevel | null>>
  setFutureRenewalConfirmation: React.Dispatch<
    React.SetStateAction<MembershipLevelFutureRenewalConfirmation | null>
  >
  setRosterCapConfirmation: React.Dispatch<
    React.SetStateAction<MembershipLevelRosterCapConfirmation | null>
  >
}

export type MembershipLevelMutations = ReturnType<typeof useMembershipLevelMutations>

function submitMembershipLevelDialog(
  dialogState: Exclude<MembershipLevelDialogState, null>,
  form: MembershipLevelForm,
  mutations: MembershipLevelMutations,
  setDialogState: React.Dispatch<React.SetStateAction<MembershipLevelDialogState>>,
  confirmationSetters: {
    setFutureRenewalConfirmation: React.Dispatch<
      React.SetStateAction<MembershipLevelFutureRenewalConfirmation | null>
    >
    setRosterCapConfirmation: React.Dispatch<
      React.SetStateAction<MembershipLevelRosterCapConfirmation | null>
    >
  },
) {
  const { setFutureRenewalConfirmation, setRosterCapConfirmation } = confirmationSetters
  if (dialogState.mode === 'edit') {
    mutations.updateLevel.mutate(
      { level: dialogState.level, form },
      {
        onError: (error) => {
          const rosterCapWarning = membershipLevelRosterCapWarning(error)
          if (rosterCapWarning) {
            mutations.updateLevel.reset()
            setDialogState(null)
            setRosterCapConfirmation({ level: dialogState.level, form, warning: rosterCapWarning })
            return
          }
          const warning = membershipLevelFutureRenewalWarning(error)
          if (!warning) return

          mutations.updateLevel.reset()
          setDialogState(null)
          setFutureRenewalConfirmation({ level: dialogState.level, form, warning })
        },
      },
    )
    return
  }

  mutations.createLevel.mutate(form)
}

function closeFutureRenewalConfirmation(
  mutations: MembershipLevelMutations,
  setFutureRenewalConfirmation: React.Dispatch<
    React.SetStateAction<MembershipLevelFutureRenewalConfirmation | null>
  >,
) {
  mutations.updateLevel.reset()
  setFutureRenewalConfirmation(null)
}

function closeRosterCapConfirmation(
  mutations: MembershipLevelMutations,
  setRosterCapConfirmation: React.Dispatch<
    React.SetStateAction<MembershipLevelRosterCapConfirmation | null>
  >,
) {
  mutations.updateLevel.reset()
  setRosterCapConfirmation(null)
}

function confirmFutureRenewalChange(
  confirmation: MembershipLevelFutureRenewalConfirmation,
  mutations: MembershipLevelMutations,
) {
  mutations.updateLevel.mutate({
    level: confirmation.level,
    form: confirmation.form,
    confirmFutureOnlyChange: true,
  })
}

function confirmRosterCapChange(
  confirmation: MembershipLevelRosterCapConfirmation,
  mutations: MembershipLevelMutations,
) {
  mutations.updateLevel.mutate({
    level: confirmation.level,
    form: confirmation.form,
    confirmRosterCapChange: true,
  })
}

function membershipLevelsTableState(isLoading: boolean, isError: boolean) {
  if (isLoading) return 'loading'
  if (isError) return 'error'

  return 'ready'
}
